import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../base/Button'

export const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const navigate = useNavigate()

  // Listing modal state
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [listForm, setListForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: '',
    userLocation: '',
    propertyLocation: '',
    contactMethod: 'whatsapp',
    message: '',
    notes: '',
    submitting: false,
  })

  // Track seller session to switch CTA to Dashboard
  const [sellerSession, setSellerSession] = useState<any>(null)
  useEffect(() => {
    const read = () => {
      try { const raw = localStorage.getItem('seller_session'); setSellerSession(raw ? JSON.parse(raw) : null) } catch { setSellerSession(null) }
    }
    read()
    const onStorage = (e: StorageEvent) => { if (e.key === 'seller_session') read() }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const handlePropertiesClick = () => {
    navigate('/properties')
    setIsMenuOpen(false)
  }

  const handleContactClick = () => {
    navigate('/contact')
    setIsMenuOpen(false)
  }

  const handleAIAssistant = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement
    if (widget) {
      widget.click()
    }
    setIsMenuOpen(false)
  }

  const handleListSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!listForm.name.trim() || (!listForm.email.trim() && !listForm.phone.trim())) {
      alert('Please provide your full name and at least email or phone.')
      return
    }
    try {
      setListForm(prev => ({ ...prev, submitting: true }))
      const res = await fetch('/api/support', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: listForm.name,
          email: listForm.email,
          phone: listForm.phone,
          role: listForm.role,
          company: listForm.company,
          userLocation: listForm.userLocation,
          propertyLocation: listForm.propertyLocation,
          contactMethod: listForm.contactMethod,
          message: listForm.message,
        }),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(`Failed to submit: ${res.status} ${text}`)
      }
      alert('Thanks! Your details have been submitted to support. We will contact you shortly.')
      setShowAuthModal(false)
      setListForm({ name: '', email: '', phone: '', role: '', company: '', userLocation: '', propertyLocation: '', contactMethod: 'whatsapp', message: '', notes: '', submitting: false })
    } catch (err: any) {
      alert(err.message || 'Failed to submit. Please try again later.')
    } finally {
      setListForm(prev => ({ ...prev, submitting: false }))
    }
  }

  const goSellerDashboard = () => {
    navigate('/seller/Dashboard')
    setIsMenuOpen(false)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-lg">
      <div className="w-full px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-14 sm:h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 cursor-pointer" style={{ fontFamily: 'Pacifico, serif' }} onClick={() => navigate('/')}>
              Step in luxury
            </h1>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-sm xl:text-base cursor-pointer"
            >
              Home
            </button>
            <button 
              onClick={handlePropertiesClick}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-sm xl:text-base cursor-pointer"
            >
              Properties
            </button>
            <button 
              onClick={handleContactClick}
              className="text-gray-700 hover:text-blue-600 transition-colors font-medium text-sm xl:text-base cursor-pointer"
            >
              Contact
            </button>
            <button 
              onClick={handleAIAssistant}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm xl:text-base cursor-pointer whitespace-nowrap"
            >
              <i className="ri-robot-2-line mr-2"></i>
              AI Assistant
            </button>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3 xl:space-x-4">
            {sellerSession?.token ? (
              <Button variant="outline" size="sm" className="text-sm xl:text-base px-4 xl:px-6 py-2 xl:py-2.5 whitespace-nowrap" onClick={goSellerDashboard}>
                Dashboard
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="text-sm xl:text-base px-4 xl:px-6 py-2 xl:py-2.5 whitespace-nowrap" onClick={() => setShowAuthModal(true)}>
                Become a Seller
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button onClick={toggleMenu} className="p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
              {isMenuOpen ? (
                <i className="ri-close-line text-2xl"></i>
              ) : (
                <i className="ri-menu-line text-2xl"></i>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Improved */}
        {isMenuOpen && (
          <div className="lg:hidden absolute left-0 right-0 top-full bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-xl z-40">
            <div className="flex flex-col py-4 px-4 space-y-1">
              {/* Navigation Links */}
              <button 
                onClick={() => navigate('/')}
                className="w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors font-medium text-base flex items-center"
              >
                <i className="ri-home-4-line mr-3 text-lg"></i>
                Home
              </button>
              
              <button 
                onClick={handlePropertiesClick}
                className="w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors font-medium text-base flex items-center"
              >
                <i className="ri-building-4-line mr-3 text-lg"></i>
                Properties
              </button>
              
              <button 
                onClick={handleContactClick}
                className="w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors font-medium text-base flex items-center"
              >
                <i className="ri-contacts-line mr-3 text-lg"></i>
                Contact
              </button>

              {/* Action Buttons */}
              <div className="border-t border-gray-200 pt-3 mt-2 space-y-2">
                <button 
                  onClick={handleAIAssistant}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-base flex items-center justify-center"
                >
                  <i className="ri-robot-2-line mr-2 text-lg"></i>
                  AI Assistant
                </button>
                
                {sellerSession?.token ? (
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full px-4 py-3 flex items-center justify-center border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => { goSellerDashboard() }}
                  >
                    Dashboard
                  </Button>
                ) : (
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full px-4 py-3 flex items-center justify-center border-gray-300 text-gray-700 hover:bg-gray-50"
                    onClick={() => {
                      setShowAuthModal(true)
                      setIsMenuOpen(false)
                    }}
                  >
                    Become a Seller
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}